import { useState } from "react";
import { FaStar, FaCarSide, FaChevronUp, FaChevronDown, FaCheck, FaUser, FaClipboardList, FaRoad } from "react-icons/fa";

export default function CabCard({ cab, priceInfo, onSelect, isSelected, gstRate = 18, isBestValue = false, distance, serviceType }) {
  const [showInclusions, setShowInclusions] = useState(false);

  // Build dynamic inclusions list from actual cab API data
  const getInclusions = () => {
    const items = [];

    // Driver allowance
    if (cab.driverAllowance !== undefined) {
      items.push({ label: `Driver Allowance: ₹${cab.driverAllowance}`, icon: "check" });
    } else {
      items.push({ label: "Driver Allowance Included", icon: "check" });
    }

    // Base fare with included km
    if (cab.includedKm) {
      items.push({
        label: `Base Fare for ${cab.includedKm} km. ₹${priceInfo?.extraKmRate || cab.extraFarePerKm || cab.extraKmRate || 0}/km beyond that.`,
        icon: "check"
      });
    }

    // Toll/Tax
    if (cab.includesTollTax || cab.tollTaxIncluded) {
      items.push({ label: "State Tax & Toll Included", icon: "check" });
    }

    // Pickup/Drop
    items.push({ label: "Only One Pickup and Drop", icon: "check" });

    // GST - use global rate from settings
    items.push({ label: `GST (${gstRate}%)`, icon: "check" });

    // Baggage
    const bags = cab.baggageCapacity || cab.luggageCapacity || 1;
    items.push({ label: `${bags} bag${bags > 1 ? 's' : ''}`, icon: "check" });

    // AC
    const hasAC = cab.acType || cab.ac || cab.airConditioned || true;
    items.push({ label: hasAC ? "Air Conditioning" : "Non-AC", icon: "check" });

    // Night charges
    if (cab.nightChargesIncluded || cab.includesNightCharges) {
      items.push({ label: "Night Charges Included", icon: "check" });
    }

    // Fuel charges
    if (cab.fuelChargesIncluded || cab.includesFuel) {
      items.push({ label: "Fuel Charges Included", icon: "check" });
    }

    // Additional features from API
    if (Array.isArray(cab.features)) {
      cab.features.forEach(f => items.push({ label: f, icon: "check" }));
    }
    if (Array.isArray(cab.inclusions)) {
      cab.inclusions.forEach(f => items.push({ label: f, icon: "check" }));
    }

    return items;
  };

  const inclusions = getInclusions();

  // Get display name and model info
  const cabName = cab.name || cab.model || "Standard Cab";
  const seatingCapacity = cab.seatingCapacity || 4;
  const hasAC = cab.acType === 'AC' || cab.ac === true || cab.airConditioned === true;
  const cabModel = cab.model || "Or equivalent";
  const cabTypeLabel = `${cabModel} | ${seatingCapacity} seater ${hasAC ? 'AC' : 'Non-AC'} Cab`;

  // Base price per km (e.g. ₹11.88/km)
  const rawBaseKmPrice = Number(priceInfo?.baseKmPrice || cab.baseKmPrice || 0);
  const baseKmRate = rawBaseKmPrice > 0
    ? (rawBaseKmPrice % 1 === 0 ? rawBaseKmPrice : rawBaseKmPrice.toFixed(2))
    : (cab.category?.toLowerCase()?.includes("innova") ? "23.50" : "11.88");

  return (
    <>
      {/* Desktop Main Cab Card */}
      <div
        onClick={onSelect}
        className={`hidden md:flex relative bg-white rounded-xl p-6 flex-col md:flex-row justify-between items-center overflow-hidden hover:shadow-2xl transition cursor-pointer border ${
          isSelected ? "border-2 border-blue-500" : "border-gray-200"
        }`}
      >
        {/* Slanted Rectangle Background Shape */}
        <div
          className="absolute top-0 right-0 w-[18%] h-full"
          style={{
            backgroundColor: "#EAF0FC",
            clipPath: "polygon(99% 0%, 100% 0%, 100% 100%, 0% 100%)"
          }}
        />
        {/* Best Value Ribbon */}
        {isBestValue && (
          <div className="absolute top-0 left-0 z-10">
            <div className="bg-green-400 text-white text-sm font-bold px-14 py-1.5 rotate-[-45deg] translate-x-[-35%] translate-y-[40%] shadow-lg">
              Best Value
            </div>
          </div>
        )}

        {/* Car Image */}
        <div className="w-full md:w-1/4 flex justify-center z-0">
          <img
            src={cab.imageUrl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3ECab%3C/text%3E%3C/svg%3E"}
            alt={cab.name}
            className="w-40 h-32 object-contain"
          />
        </div>

        {/* Car Details */}
        <div className="flex-1 min-w-0 px-0 md:px-6 text-gray-900 z-0">
          <h3 className="text-xl font-bold flex items-center gap-2">
            {cabName}
            {cab.driverRating > 0 ? (
              <span className="bg-blue-600 text-white text-xs px-2 py-[2px] rounded flex items-center gap-1">
                {cab.driverRating.toFixed(1)} <FaStar size={10} />
              </span>
            ) : (
              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-[2px] rounded">
                New
              </span>
            )}
          </h3>

          <p className="text-gray-600 text-sm">
            {cabTypeLabel}
          </p>

          <p className="text-gray-500 text-sm flex items-center gap-1">
            <FaUser size={12} className="text-gray-400" />
            Driver allowance included
          </p>

          {/* Google Map Distance + Included KM */}
          {(priceInfo?.includedKm || distance > 0) && (
            <div className="flex flex-col gap-1 my-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800 whitespace-nowrap">
                  <FaClipboardList size={13} className="text-gray-400 shrink-0" />
                  <span>{priceInfo?.includedKm || Math.round(distance)} kms included</span>
                </span>

                {baseKmRate && (
                  <>
                    <span className="text-gray-300 select-none">|</span>
                    <span className="font-bold text-gray-900 whitespace-nowrap">
                      ₹{baseKmRate}/km
                    </span>
                  </>
                )}

                <span className="text-gray-300 select-none">|</span>

                <span className="inline-flex items-center gap-1 text-gray-600 whitespace-nowrap">
                  <span className="text-gray-500 font-medium">Post limit</span>
                  <span className="font-bold text-gray-900">
                    ₹{priceInfo?.extraKmRate || cab.extraFarePerKm || cab.extraKmRate || "0"}/{cab.serviceType === "rental" ? "hr" : "km"}
                  </span>
                </span>
              </div>
              {priceInfo?.roundTripMinApplied && (
                <div className="flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded w-fit">
                  <span>Round trip min. {priceInfo.minKmPerDay} km/day applied ({priceInfo.numDays} days = {priceInfo.minTotalKm} km)</span>
                </div>
              )}
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowInclusions(!showInclusions);
            }}
            className="flex items-center gap-1 text-blue-500 hover:text-blue-600 font-medium text-sm transition-colors"
          >
            Inclusions and Exclusions
            {showInclusions ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>
        </div>

        {/* Price + Button */}
        <div className="flex flex-col items-end gap-2 text-gray-900 z-0">
          <div className="text-right">
            {/* Strikethrough original price + discount badge on same line */}
            {(cab.discount > 0 || priceInfo?.discount > 0) && (
              <div className="flex items-center gap-2 justify-end mb-1">
                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap">
                  {cab.discount || priceInfo?.discount}% OFF
                </span>
                <span className="text-red-400 text-sm line-through">
                  ₹{priceInfo?.originalPrice || cab.price || "0"}
                </span>
              </div>
            )}
            
            {/* Discounted price on its own line */}
            <h3 className="text-2xl font-bold">
              ₹{priceInfo?.totalPrice || 
                 (() => {
                   const originalPrice = priceInfo?.originalPrice || cab.price || 0;
                   const discountPercent = cab.discount || priceInfo?.discount || 0;
                   return Math.round(originalPrice - (originalPrice * discountPercent / 100));
                 })()}
            </h3>
            
            <p className="text-gray-500 text-xs mt-1">
              +₹{Math.round((priceInfo?.totalPrice || (() => {
                   const originalPrice = priceInfo?.originalPrice || cab.price || 0;
                   const discountPercent = cab.discount || priceInfo?.discount || 0;
                   return Math.round(originalPrice - (originalPrice * discountPercent / 100));
                 })()) * gstRate / 100)} taxes & fees ({gstRate}% GST)
            </p>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className="bg-orange-500 hover:bg-orange-600 px-5 py-2 rounded-lg font-semibold flex items-center gap-2 text-white transition-colors whitespace-nowrap"
          >
            <img 
              src="/icons/details-icon.png" 
              alt="details icon"
              className="w-5 h-4 object-contain"
            />
            Select Car
          </button>
        </div>
      </div>

      {/* Mobile Cab Card (MakeMyTrip Style) */}
      <div
        onClick={onSelect}
        className={`flex md:hidden flex-col bg-white rounded-xl p-4 border transition-all cursor-pointer relative overflow-hidden ${
          isSelected ? "border-blue-600 bg-blue-50/10 shadow-sm" : "border-gray-200 shadow-sm hover:shadow-md"
        }`}
      >
        {/* Top-right diagonal label for "Best Value" if applicable */}
        {isBestValue && (
          <div className="absolute top-0 right-0 z-10 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl shadow-sm">
            Best Value
          </div>
        )}

        {/* Top Section: Details Left & Image Right */}
        <div className="flex justify-between items-start gap-3 w-full">
          {/* Details Left (65%) */}
          <div className="flex-1 min-w-0">
            {/* Cab Title & Rating */}
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-bold text-gray-900 text-base leading-tight truncate">
                {cabName}
              </h4>
              {cab.driverRating > 0 ? (
                <span className="bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                  {cab.driverRating.toFixed(1)} <FaStar size={8} />
                </span>
              ) : (
                <span className="bg-gray-100 text-gray-600 text-[10px] px-1.5 py-0.5 rounded font-semibold">
                  New
                </span>
              )}
            </div>

            {/* Cab Category / Model description */}
            <p className="text-gray-500 text-xs mb-2 leading-snug">
              {cabModel}
            </p>

            {/* Spec Badges Grid */}
            <div className="flex flex-wrap gap-1 mb-2.5">
              <span className="bg-gray-100 text-gray-600 text-[9px] px-2 py-0.5 rounded font-medium">
                {seatingCapacity} Seats
              </span>
              <span className="bg-gray-100 text-gray-600 text-[9px] px-2 py-0.5 rounded font-medium">
                {cab.baggageCapacity || cab.luggageCapacity || 1} Bag(s)
              </span>
              <span className="bg-gray-100 text-gray-600 text-[9px] px-2 py-0.5 rounded font-medium">
                {hasAC ? "AC" : "Non-AC"}
              </span>
              {cab.fuelType && (
                <span className="bg-gray-100 text-gray-600 text-[9px] px-2 py-0.5 rounded font-medium">
                  {cab.fuelType}
                </span>
              )}
            </div>

            {/* Distance / Limit Details */}
            {(priceInfo?.includedKm || distance > 0) && (
              <div className="text-xs text-gray-600 font-semibold mb-1 flex flex-wrap items-center gap-1">
                <span className="text-gray-900 font-bold bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded border border-blue-100 whitespace-nowrap">
                  {priceInfo?.includedKm || Math.round(distance)} km included
                </span>
                {baseKmRate && (
                  <span className="text-gray-900 font-bold bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 whitespace-nowrap">
                    ₹{baseKmRate}/km
                  </span>
                )}
                {priceInfo?.roundTripMinApplied && (
                  <span className="text-[10px] text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 font-medium whitespace-nowrap">
                    Min {priceInfo.minKmPerDay} km/day ({priceInfo.numDays}d)
                  </span>
                )}
              </div>
            )}
            <p className="text-[10px] text-gray-500 font-medium whitespace-nowrap">
              Extra km fare: <span className="font-bold text-gray-800">₹{priceInfo?.extraKmRate || cab.extraFarePerKm || cab.extraKmRate || "0"}/{serviceType === "rental" ? "hr" : "km"}</span> after limit
            </p>
          </div>

          {/* Car Image Right (35%) */}
          <div className="w-[30%] max-w-[110px] flex-shrink-0 flex flex-col items-center justify-center pt-2">
            <img
              src={cab.imageUrl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='100'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3C/svg%3E"}
              alt={cab.name}
              className="w-full h-16 object-contain"
            />
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-200 my-3 w-full"></div>

        {/* Bottom Section: Inclusions link & Booking / Price CTA */}
        <div className="flex justify-between items-center w-full">
          {/* Details / Inclusions Link */}
          <div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowInclusions(!showInclusions);
              }}
              className="text-xs text-blue-600 hover:text-blue-700 font-bold flex items-center gap-0.5"
            >
              Inclusions & Details
              {showInclusions ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            </button>
            <p className="text-[10px] text-green-600 font-medium mt-0.5 flex items-center gap-0.5">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block"></span>
              Free Cancellation
            </p>
          </div>

          {/* Price & CTA Button */}
          <div className="text-right flex items-center gap-3">
            <div>
              {/* Discount price display */}
              <div className="flex items-center gap-1.5 justify-end">
                {(cab.discount > 0 || priceInfo?.discount > 0) && (
                  <>
                    <span className="text-[10px] bg-green-100 text-green-800 px-1 py-0.2 rounded font-bold">
                      {cab.discount || priceInfo?.discount}% OFF
                    </span>
                    <span className="text-gray-400 text-xs line-through">
                      ₹{priceInfo?.originalPrice || cab.price || "0"}
                    </span>
                  </>
                )}
              </div>
              <h3 className="text-xl font-black text-gray-900">
                ₹{priceInfo?.totalPrice || 
                   (() => {
                     const originalPrice = priceInfo?.originalPrice || cab.price || 0;
                     const discountPercent = cab.discount || priceInfo?.discount || 0;
                     return Math.round(originalPrice - (originalPrice * discountPercent / 100));
                   })()}
              </h3>
              <p className="text-[9px] text-gray-400 font-bold uppercase mt-0.5">
                + GST & Tolls
              </p>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm ${
                isSelected
                  ? "bg-blue-600 text-white shadow-blue-100"
                  : "bg-orange-500 text-white shadow-orange-100 hover:bg-orange-600"
              }`}
            >
              {isSelected ? "Selected" : "Select"}
            </button>
          </div>
        </div>
      </div>

      {/* Dropdown Content - OUTSIDE the card */}
      {showInclusions && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-blue-50 rounded-lg p-4 border border-blue-100 mt-2 mb-4"
        >
          <h4 className="text-md font-semibold text-gray-900 mb-3">
            Inclusions and Exclusions
          </h4>
          <div className="space-y-2">
            {inclusions.map((item, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="flex-shrink-0 w-4 h-4 mt-0.5">
                  <FaCheck className="text-green-500" size={14} />
                </div>
                <p className="text-gray-700 text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
